<script setup lang="ts">
import { ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryUnit, type UnitCondition } from '@/api/inventory';
import { apiErrorText } from '@/utils/api-errors';
import { CONDITIONS } from '@/utils/inventory';

const props = defineProps<{ unit: InventoryUnit }>();
const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const condition = ref<UnitCondition>(props.unit.condition);
const error = ref('');

const save = useMutation({
  mutationFn: () => inventoryApi.updateUnit(props.unit.id, { condition: condition.value }),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['inventoryUnits'] });
    queryClient.invalidateQueries({ queryKey: ['inventoryAlerts'] });
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo actualizar la condición.');
  },
});
</script>
<template>
  <v-card title="Actualizar condición">
    <v-card-text class="pt-5">
      <p class="mb-3 text-body-2">
        <b>{{ unit.itemName }}</b>
        <span class="text-medium-emphasis"> · {{ unit.identifier || 'Sin identificador' }}</span>
      </p>
      <v-radio-group
        v-model="condition"
        density="compact"
        hide-details
      >
        <v-radio
          v-for="option in CONDITIONS"
          :key="option.value"
          :value="option.value"
          :color="option.color"
          :label="option.title"
        />
      </v-radio-group>
      <p class="mt-3 text-caption text-medium-emphasis">
        «Fuera de servicio» abre una alerta y avisa por correo; al volver a «Operativo» se resuelve.
      </p>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="condition === unit.condition"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        Guardar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
